import { Avatar, Button, Card, CardActions, CardContent, CardHeader, Chip, Skeleton } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { FunctionComponent } from 'react';
import { UserModel } from '../types/UserModel';
import { Link } from 'react-router';
import { Label } from '@/components/Label';
import { useTranslation } from '@/lib';
import { UserType } from '../api/users/users.contracts';
import { Permission } from '../submodules/roles/api/permissions.contracts';

interface UserListProps {
  users: UserModel[] | undefined;
  isLoading: boolean;
  isReadOnly?: boolean;
}

export const UserList: FunctionComponent<UserListProps> = ({ users = [], isLoading, isReadOnly = false }) => {
  const { t } = useTranslation();

  const getUserTypeLabel = (type: UserType) => {
    switch (type) {
      case UserType.GUEST:
        return t('user_type_guest');
      case UserType.OPERATOR:
        return t('user_type_operator');
      default:
        return t('user_type_operator'); // Default fallback
    }
  };

  const getPermissionLabel = (permission: Permission): string => {
    switch (permission) {
      case Permission.LOCATIONS_VIEW:
        return t('view_locations');
      case Permission.TRANSACTIONS_VIEW:
        return t('view_transactions');
      case Permission.TRANSACTIONS_EDIT:
        return t('edit_transactions');
      default:
        return permission;
    }
  };

  return (
    <div className="grid w-full grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4">
      {isLoading || users === undefined ? (
        <>
          {Array(15)
            .fill(1)
            .map((val, i) => (
              <Skeleton key={i} variant="rounded" width={'full'} height={120} />
            ))}
        </>
      ) : (
        users.map((u) => {
          return (
            <div className="w-full" key={u.id}>
              <Card variant="outlined" title={u.name}>
                <Link to={{ pathname: u.id.toString() }} className="text-inherit no-underline">
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: blueGrey[500] }} aria-label={t('user_avatar', { name: u.name })}>
                        {u.name[0]}
                      </Avatar>
                    }
                    title={u.name}
                    subheader={u.email}
                    action={
                      <Label variant="soft" color={u.isActive ? 'success' : 'error'}>
                        {u.isActive ? t('active') : t('inactive')}
                      </Label>
                    }
                  />
                </Link>
                <CardContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
                  <div className="flex flex-wrap gap-2">
                    <Chip color="primary" variant="outlined" size="small" label={getUserTypeLabel(u.type)} />
                    {u.type !== UserType.GUEST && <Chip color="secondary" variant="outlined" size="small" label={u.roles[0].description} />}
                    {u.type === UserType.GUEST &&
                      u.roles.length > 0 &&
                      u.roles[0].dto.permissions.map((permission) => (
                        <Chip
                          key={permission.name}
                          color="secondary"
                          variant="outlined"
                          size="small"
                          label={getPermissionLabel(permission.name)}
                        />
                      ))}
                  </div>
                  {/* <div className="flex flex-wrap gap-2">
                    {u.roles.map((role) => {
                      return (
                        <Chip
                          key={role.id}
                          color="secondary"
                          variant="outlined"
                          label={role.nameInTitleCase}
                          className={'cursor-pointer'}
                          onDelete={console.log}
                        />
                      );
                    })}
                  </div> */}
                </CardContent>
                <CardActions>
                  {!isReadOnly && (
                    <Button variant="text" component={Link} to={{ pathname: u.id.toString() }}>
                      {t('edit')}
                    </Button>
                  )}
                  {/* <Button color="secondary">Delete</Button> */}
                </CardActions>
              </Card>
            </div>
          );
        })
      )}
    </div>
  );
};
